import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import {
    MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME,
    MESSAGING_TEMPLATE_DCUI_REQUIRED_VARIABLE,
    MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME,
    MESSAGING_TEMPLATE_PIN_REQUIRED_VARIABLE
} from "@constants/messaging-template-default";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import {
    MessagingTemplateContentValidationErrors,
    MessagingTemplateGeneralInfoValidationErrors,
    MessagingTemplateValidationErrors,
    MessagingTemplateVariablesMultiLanguageValidationErrors,
    MessagingTemplateVariablesValidationErrors
} from "@idw-types/common/validation/messaging-template/messaging-template-validation-errors";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";
import { convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { getErrorAlert } from "@utils/tab-util";
import { getMessagingTemplateVariables } from "@utils/messaging-template-util";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";


export const getMessagingTemplateValidationErrors = (messagingTemplate: FormattedMessagingTemplate): MessagingTemplateValidationErrors => {
    const generalInfoErrors = getMessagingTemplateGeneralInfoValidationErrors(messagingTemplate);
    const contentErrors = getMessagingTemplateContentValidationErrors(messagingTemplate);
    const variablesErrors = getMessagingTemplateVariablesValidationErrors(messagingTemplate);

    const count = generalInfoErrors.count + contentErrors.count + variablesErrors.count;

    return {
        count,
        generalInfo: generalInfoErrors,
        content: contentErrors,
        variables: variablesErrors,
        contentAndVariables: {
            count: contentErrors.count + variablesErrors.count,
            tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messagingTemplateDetails.tabs.content.tabName} errorCount={contentErrors.count + variablesErrors.count} />
        }
    };
};

const getMessagingTemplateGeneralInfoValidationErrors = (messagingTemplate: FormattedMessagingTemplate): MessagingTemplateGeneralInfoValidationErrors => {
    let nameError: React.ReactNode = null;
    let count = 0;
    let companyError = null;
    let relyingPartyError = null;
    let templateAvailabilityLevelError = null;
    const { templateAvailabilityLevel, relyingParty, company } = messagingTemplate;

    if (!messagingTemplate.name) {
        count++;
        nameError = getErrorAlert(resources.common.requiredField);
    }

    if (templateAvailabilityLevel === null) {
        templateAvailabilityLevelError = getErrorAlert(resources.common.requiredField);
        count++;
    } else {
        if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION && company === null) {
            companyError = getErrorAlert(resources.common.requiredField);
            count++;
        } else if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.RELYING_PARTY) {
            if (relyingParty === null) {
                relyingPartyError = getErrorAlert(resources.common.requiredField);
                count++;
            }

            if (company === null) {
                companyError = getErrorAlert(resources.common.requiredField);
                count++;
            }

        }
    }

        return {
            count,
            nameError,
            templateAvailabilityLevelError,
            companyError,
            relyingPartyError,
            tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messagingTemplateDetails.tabs.generalInfo.tabName} errorCount={count} />
        };
    };


export const getMessagingTemplateContentValidationErrors = (messagingTemplate: FormattedMessagingTemplate): MessagingTemplateContentValidationErrors => {
    let count = 0;
    let requiredVariableError = null;

    if (
        [MessagingTemplateTypes.DCUI_EMAIL, MessagingTemplateTypes.DCUI_SMS].includes(messagingTemplate.type) &&
        !messagingTemplate.template.includes(MESSAGING_TEMPLATE_DCUI_REQUIRED_VARIABLE)
    ) {
        count++;
        requiredVariableError = (
            <ErrorAlert id={`messaging-template-required-variable-error`}>
                <>
                    <div>{`The following text must appear in your template:`}</div>
                    <p>{MESSAGING_TEMPLATE_DCUI_REQUIRED_VARIABLE}</p>
                    <div>{`This text will be substituted in the message sent to the user. The user will use the substituted URL to access DCUI.`}</div>
                </>
            </ErrorAlert>
        );
    } else if (
        [MessagingTemplateTypes.PIN_EMAIL, MessagingTemplateTypes.PIN_SMS].includes(messagingTemplate.type) &&
        !messagingTemplate.template.includes(MESSAGING_TEMPLATE_PIN_REQUIRED_VARIABLE)
    ) {
        count++;
        requiredVariableError = (
            <ErrorAlert id={`messaging-template-required-variable-error`}>
                <>
                    <div>{`The following text must appear in your template:`}</div>
                    <p>{MESSAGING_TEMPLATE_PIN_REQUIRED_VARIABLE}</p>
                    <div>{`This text will be substituted in the message sent to the user. The user will use the substituted PIN code to verify their email or phone number.`}</div>
                </>
            </ErrorAlert>
        );
    }

    return {
        count,
        requiredVariableError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messagingTemplateDetails.tabs.content.tabName} errorCount={count} />
    };
};

export const getMessagingTemplateVariablesValidationErrors = (messagingTemplate: FormattedMessagingTemplate): MessagingTemplateVariablesValidationErrors => {
    let count = 0;
    const multiLanguageEmailSubjectErrors: MessagingTemplateVariablesMultiLanguageValidationErrors = {};
    const multiLanguageValidationErrors: MessagingTemplateVariablesMultiLanguageValidationErrors = {};

    const availableLanguages = Object.keys(messagingTemplate.languageFiles) as Array<LocaleTypes>;

    availableLanguages.forEach(locale => {
        let flattenedLanguageFile: { [key: string]: string } = {};
        if (messagingTemplate.languageFiles[locale]) {
            flattenedLanguageFile = convertJSONObjectToDotSeparatedString(messagingTemplate.languageFiles[locale]);
        }
        const finalVariables: Array<{ name: string; value: string; }> =  getMessagingTemplateVariables(messagingTemplate.languageFiles, messagingTemplate.template, locale);

        const shouldHaveDCUISubjectVariable = messagingTemplate.type === MessagingTemplateTypes.DCUI_EMAIL;
        const shouldHavePinSubjectVariable = messagingTemplate.type === MessagingTemplateTypes.PIN_EMAIL;
        let hasDCUISubjectVariable = false;
        let hasPinSubjectVariable = false;

        if (shouldHaveDCUISubjectVariable && flattenedLanguageFile[MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME]) {
            hasDCUISubjectVariable = true;
        } else if (shouldHavePinSubjectVariable && flattenedLanguageFile[MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME]) {
            hasPinSubjectVariable = true;
        }

        multiLanguageValidationErrors[locale] = { errors: {}, count: 0 };
        finalVariables.forEach(variable => {
            if (!variable.value) {
                multiLanguageValidationErrors[locale].errors[variable.name] = getErrorAlert(resources.common.requiredField, `required-variable-${variable.name}`);
                multiLanguageValidationErrors[locale].count += 1;
                count++;
            }
        });

        multiLanguageEmailSubjectErrors[locale] = { errors: {}, count: 0 };
        if (shouldHaveDCUISubjectVariable && !hasDCUISubjectVariable) {
            multiLanguageEmailSubjectErrors[locale].errors["subject"] = getErrorAlert(resources.common.requiredField, `required-variable-dcuiEmail.subject`);
            multiLanguageEmailSubjectErrors[locale].count += 1;
            count++;
        } else if (shouldHavePinSubjectVariable && !hasPinSubjectVariable) {
            multiLanguageEmailSubjectErrors[locale].errors["subject"] = getErrorAlert(resources.common.requiredField, `required-variable-emailpin.subject`);
            multiLanguageEmailSubjectErrors[locale].count += 1;
            count++;
        }
    });

    return {
        count: count,
        multiLanguageValidationErrors,
        multiLanguageEmailSubjectErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messagingTemplateDetails.tabs.variables.tabName} errorCount={count} />
    };
};